import styles from "./toolbox-component-dropdown.module.css";
import React, { useRef, useEffect, useState } from "react";
import { TwitterPicker } from "react-color";
import { createPortal } from "react-dom";

type DropdownPosition = "below" | "right";
type ChevronDirection = "vertical" | "horizontal";

interface ToolboxComponentDropdownProps {
  isVisible: boolean;
  onToggleVisibility: () => void;
  label: React.ReactNode;
  onMouseLeave?: () => void;
  children: React.ReactNode;
  dropUp?: boolean;
  position?: DropdownPosition;
  chevronDirection?: ChevronDirection;
  maxHeight?: number;
}

export const ToolboxComponentDropdown = (
  props: ToolboxComponentDropdownProps
) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [labelPosition, setLabelPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (props.isVisible && containerRef.current && overlayRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const position = props.position ?? "below";
      const offsetX = position === "right" ? 16 : 10;

      if (props.dropUp) {
        // For drop-up, position above the button
        // We'll set a negative bottom offset which we'll apply differently
        setLabelPosition({
          top: rect.top + window.scrollY, // Will be adjusted with transform
          left: rect.left + window.scrollX + offsetX,
        });
      } else if (position === "right") {
        // Position to the right of the label, vertically centered
        const overlayHeight = overlayRef.current.offsetHeight || 400;
        const labelCenterY = rect.top + rect.height / 2;
        const top = labelCenterY - overlayHeight / 2 + window.scrollY;

        setLabelPosition({
          top: Math.max(10, top), // Ensure it doesn't go above viewport
          left: rect.right + window.scrollX + offsetX,
        });
      } else {
        // Default: position below the label
        setLabelPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX + offsetX,
        });
      }
    }
  }, [props.isVisible, props.dropUp, props.position]);

  useEffect(() => {
    // Only add listener when dropdown is visible
    if (!props.isVisible) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        overlayRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !overlayRef.current.contains(event.target as Node)
      ) {
        props.onToggleVisibility();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isVisible]);

  return (
    <>
      <div ref={containerRef} className={styles.dropDownContainer}>
        <button
          className={styles.labelButton}
          onClick={() => {
            props.onToggleVisibility();
          }}
        >
          {props.label}
        </button>
      </div>
      {props.isVisible &&
        createPortal(
          <div
            id="dropdown-portal"
            ref={overlayRef}
            className={`${styles.dropdown} ${
              props.isVisible ? styles.open : ""
            }`}
            style={{
              position: "absolute",
              top: labelPosition.top,
              left: labelPosition.left,
              zIndex: 1000,
              ...(props.dropUp && { transform: "translateY(-100%)" }),
              ...(props.maxHeight && { maxHeight: props.maxHeight, overflowY: "auto" }),
            }}
            onMouseLeave={() => {
              if (props.onMouseLeave) {
                props.onMouseLeave();
              }
            }}
          >
            {props.children}
          </div>,
          document.body
        )}
    </>
  );
};
